import React from 'react';
import Typography from '@material-ui/core/Typography';
import CustomTooltip from './CustomTooltip';
import CopyReferral from '../CopyReferral';
import formatShareRate from '../../utils/formatShareRate';
import useStyles from './SupporterChart.styles';
import transformChartData from './../../utils/transformChartData';
import genChartYAxisTicks from './../../utils/genChartYAxisTicks';
import genChartYAxisDomain from './../../utils/genChartYAxisDomain';
import kNoFormatter from './../../utils/kNoFormatter';
import genShareableLink from '../../utils/getShareableLink';
import SupporterChartSkeleton from '../skeleton/SupporterChartSkeleton';
import {EarnedProduct} from '../../../../../shared/types';
import {Icon} from 'epic-diesel-common';
import {Moment} from 'moment';
import {useGetMessages} from '../../../../hooks';
import isFortnite from '../../utils/isFortnite';
import {
    CartesianGrid,
    ResponsiveContainer,
    ComposedChart,
    XAxis,
    YAxis,
    Bar,
    Tooltip,
    Label
} from 'recharts';

const CODES = {
    totalSupporters: 'epic.affiliate.earnings.my_earnings.chart.header.uniqueSupporters.v2',
    estimatedEarnings: 'epic.affiliate.earnings.my_earnings.chart.header.estimatedEarnings.v2',
    emptyChart: 'epic.affiliate.earnings.my_earnings.chart.empty.v2',
    supporters: 'epic.affiliate.earnings.card.supporters',
    shareRate: 'epic.affiliate.earnings.sort_share_rate',
    fnShareRate: 'epic.affiliate.earnings.my_earnings.fortnite_share_rate',
    copyLink: 'epic.affiliate.earnings.card.copy_link',
    copyLinkSuccess: 'epic.affiliate.earnings.card.copy_link.success',
    copyLinkFail: 'epic.affiliate.earnings.card.copy_link.failed',
    copyLinkDisabled: 'epic.affiliate.earnings.card.copy_link.disabled.v2'
};

const defaultEarnedProductPlaceholder: EarnedProduct = {
    data: [],
    totalEstimatedEarnings: 0,
    totalUniqueSupporters: 0,
    hideSupporters: false,
    reportedEarnings: 0,
    title: 'Fortnite',
    icon: '',
    link: '',
    urlSlug: 'fortnite',
    namespace: 'fn'
};

export interface SupporterChartProps {
    item: EarnedProduct;
    date: Date | Moment;
    enableLink?: boolean;
    isRendering?: boolean;
    accountId: string;
    baseStoreUrl: string;
}

export const SupporterChart: React.FC<SupporterChartProps> = ({
    item = defaultEarnedProductPlaceholder,
    date,
    enableLink = true,
    isRendering = false,
    baseStoreUrl,
    accountId
}) => {
    const styles = useStyles();
    const messages = useGetMessages(CODES);

    if (isRendering) {
        return <SupporterChartSkeleton />;
    }

    const {
        data = [],
        totalEstimatedEarnings = 0,
        totalUniqueSupporters = 0,
        hideSupporters,
        title = 'Fortnite',
        icon,
        namespace
    } = item || {};

    // Rechart Data Formatting
    const isPlaceholder = !data.length;
    const dateFormatter = dateStr => dateStr.substring(5, dateStr.length); // MM-DD
    const chartData = transformChartData(data, date);
    const yAxisDomain = genChartYAxisDomain(isPlaceholder, chartData);
    const yAxisTicks = genChartYAxisTicks(isPlaceholder, yAxisDomain[1]);
    const shareableLink = genShareableLink(item, baseStoreUrl, accountId);

    // Chart Header (Metrics)
    // const currency = (chartData[0] && chartData[0].currency) || 'USD';
    const supporters = hideSupporters
        ? 'N/A'
        : Number(totalUniqueSupporters).toLocaleString('en-US', {
              minimumFractionDigits: 0,
              maximumFractionDigits: 0
          });

    const estEarnings = !totalEstimatedEarnings
        ? 'N/A'
        : Number(totalEstimatedEarnings).toLocaleString('en-US', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
          });

    // const earnings = formatCurrency(totalEstimatedEarnings);
    const noMonthlyData = isPlaceholder || (!hideSupporters && !totalUniqueSupporters);
    const formattedShareRate = formatShareRate(item, messages);

    return (
        <div className={styles.root}>
            <ul className={styles.chartHeader}>
                <li>
                    <div className={styles.gameIcon}>
                        {isFortnite(namespace) ? (
                            <Icon icon="FORTNITE" className={styles.gameIcon} />
                        ) : !icon ? (
                            <Icon icon="EPIC" className={styles.gameIcon} fontSize="large" />
                        ) : (
                            <img src={icon} alt={title} />
                        )}
                    </div>
                    <div className={styles.gameName}>
                        <Typography variant="h4">{title}</Typography>
                        <span>{formattedShareRate}</span>
                    </div>
                </li>
                <li>
                    <Typography variant="h4">{supporters}</Typography>
                    <span>{messages.totalSupporters}</span>
                </li>
                <li>
                    <Typography variant="h4">{estEarnings}</Typography>
                    <span>{messages.estimatedEarnings}</span>
                </li>
                <li>
                    <CopyReferral
                        link={shareableLink}
                        enableLink={enableLink}
                        copyReferralLink={messages.copyLink}
                        copyReferralLinkSuccess={messages.copyLinkSuccess}
                        copyReferralLinkFailed={messages.copyLinkFail}
                        trackEventProps={{
                            interactionType: 'Click',
                            eventCategory: 'MediaCreator',
                            eventAction: 'ClickSupporterChartCopyReferralLink'
                        }}
                    />
                </li>
            </ul>
            <div className={styles.chartContent}>
                <ResponsiveContainer className={styles.rechartsWrapper}>
                    <ComposedChart
                        data={chartData}
                        barCategoryGap={1}
                        margin={{top: 5, right: 0, left: 20, bottom: 25}}>
                        <defs>
                            <linearGradient
                                id="e"
                                x1="0%"
                                y1="0%"
                                x2="0%"
                                y2="100%"
                                gradientUnits="userSpaceOnUse">
                                <stop stopColor="#3a3a3a" stopOpacity={0.15} offset={0.05} />
                                <stop stopColor="#3a3a3a" stopOpacity={0.55} offset={0.05} />
                                <stop stopColor="#3a3a3a" stopOpacity={0.15} offset={0.05} />
                            </linearGradient>
                        </defs>
                        <CartesianGrid
                            vertical={false}
                            stroke="url(#e)"
                            // fill="url(#linear)"
                        />
                        <XAxis
                            dataKey="date"
                            // tickCount={6}
                            reversed={true}
                            minTickGap={100}
                            padding={{left: 25, right: 25}}
                            axisLine={false}
                            tickLine={false}
                            tickMargin={25}
                            stroke="#606060"
                            interval="preserveStart"
                            tickFormatter={dateFormatter}>
                            {noMonthlyData && (
                                <Label className={styles.emptyChart} position="center">
                                    {messages.emptyChart}
                                </Label>
                            )}
                        </XAxis>
                        <YAxis
                            yAxisId="right"
                            orientation="right"
                            stroke="#606060"
                            axisLine={false}
                            tickLine={false}
                            minTickGap={0}
                            ticks={yAxisTicks}
                            domain={yAxisDomain}
                            tickFormatter={
                                // 1000 -> 1K
                                tickItem => kNoFormatter(tickItem)
                            }
                        />

                        {!noMonthlyData && (
                            <Tooltip content={<CustomTooltip subtext={messages.supporters} />} />
                        )}

                        <Bar
                            dataKey="referrals"
                            yAxisId="right"
                            fill={'#56198E'}
                            strokeWidth={10}
                        />
                    </ComposedChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};

export default SupporterChart;
